import { useState } from 'react';
import { evaluate, verdict } from '../lib/flip';
import { daysBetween, percent, pounds } from '../lib/format';
import { STATUSES, STATUS_LABEL, type Status } from '../lib/types';
import { checkProgress } from '../lib/checklist';
import { useStore } from '../state/store';
import { go } from '../state/useHashRoute';

export function PipelinePage() {
  const { data, updateCar, deleteCar, addDemoCars } = useStore();
  const [filter, setFilter] = useState<Status | 'all'>('all');
  const cars = data.cars.filter((c) => filter === 'all' || c.status === filter);
  const count = (s: Status) => data.cars.filter((c) => c.status === s).length;

  return (
    <div className="page">
      <div className="page-head">
        <h1>My cars</h1>
        <button type="button" className="btn primary" onClick={() => go('/calculator')}>+ Plan a car</button>
      </div>

      {data.cars.length === 0 ? (
        <div className="card empty">
          <h2>No cars yet</h2>
          <p>Plan your first car in the calculator, or load three made-up cars to see how the app works.</p>
          <div className="actions">
            <button type="button" className="btn primary" onClick={() => go('/calculator')}>Open the calculator</button>
            <button type="button" className="btn" onClick={addDemoCars}>Load example cars</button>
          </div>
        </div>
      ) : (
        <>
          <div className="chips" role="group" aria-label="Filter by status">
            <button type="button" className={`chip ${filter === 'all' ? 'on' : ''}`} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All ({data.cars.length})</button>
            {STATUSES.map((s) => (
              <button key={s} type="button" className={`chip ${filter === s ? 'on' : ''}`} aria-pressed={filter === s} onClick={() => setFilter(s)}>
                {STATUS_LABEL[s]} ({count(s)})
              </button>
            ))}
          </div>
          <ul className="car-list">
            {cars.map((c) => {
              const r = evaluate(c.deal);
              const v = verdict(r, data.settings.targets);
              const held = daysBetween(c.boughtOn, c.soldOn);
              return (
                <li key={c.id} className="card car">
                  <div className="car-main">
                    <h2><a href={`#/car/${c.id}`}>{c.title}</a></h2>
                    <p className="muted">{[c.reg, c.source].filter(Boolean).join(' · ') || 'No reg or source yet'}</p>
                    <p className="muted small">Checklist {Math.round(checkProgress(c.checks) * 100)}%{held !== null ? ` · held ${held} days` : ''}</p>
                  </div>
                  <div className="car-nums">
                    <span className={`big ${r.profit > 0 ? 'pos' : r.profit < 0 ? 'neg' : ''}`}>{pounds(r.profit)}</span>
                    <span className="muted small">{percent(r.roiPct)} return · cash in {pounds(r.cashInvested)}</span>
                    <span className={`badge ${v}`}>{v === 'good' ? 'Worth doing' : v === 'marginal' ? 'Marginal' : 'Avoid'}</span>
                  </div>
                  <div className="car-actions">
                    <label className="sr-only" htmlFor={`st-${c.id}`}>Status of {c.title}</label>
                    <select id={`st-${c.id}`} value={c.status} onChange={(e) => updateCar(c.id, { status: e.target.value as Status })}>
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                    <button type="button" className="btn small danger" onClick={() => { if (window.confirm(`Delete "${c.title}"? This cannot be undone.`)) deleteCar(c.id); }}>Delete</button>
                  </div>
                </li>
              );
            })}
          </ul>
          {cars.length === 0 && <p className="muted">No cars with that status.</p>}
        </>
      )}
    </div>
  );
}
